import React, { useEffect, useState } from "react"
import { Box, Grid, Typography, Divider } from "@mui/material"
import { useCompany, useGlobalLoader, useSnackbar } from "src/providers"
import {
  getFinancialInfo,
  putFinancialInfo,
  FinancialInfoResponse,
  Address
} from "src/services/api/companyGroups"
import { LabeledField, SectionHeader } from "./companyInfoComponents"

const isBlank = (s: string | null | undefined) => !s || !String(s).trim()

export const BillingInformationPanel = () => {
  const { companyId } = useCompany()
  const { snackbarShowMessage } = useSnackbar()
  const [financialInfo, setFinancialInfo] = useState<FinancialInfoResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const { showLoader, hideLoader } = useGlobalLoader()

  useEffect(() => {
    setLoading(true)
    showLoader()
    getFinancialInfo(companyId)
      .then(setFinancialInfo)
      .catch(() => setFinancialInfo(null))
      .finally(() => {
        setLoading(false)
        hideLoader()
      })
  }, [companyId])

  const address = financialInfo?.address
  const errors = {
    vatNum: isBlank(financialInfo?.vatNum),
    street: isBlank(address?.street),
    streetNum: isBlank(address?.streetNum),
    postalCode: isBlank(address?.postalCode),
    city: isBlank(address?.city),
    country: isBlank(address?.country)
  }
  const saveDisabled = Object.values(errors).some(Boolean)

  const onSave = () => {
    if (!financialInfo || saveDisabled) return
    const payload = {
      vatNum: financialInfo.vatNum,
      address: financialInfo.address,
      paymentThreshold: financialInfo.paymentThreshold,
      currency: financialInfo.currency
    }
    putFinancialInfo(companyId, payload)
      .then(() => snackbarShowMessage("Billing information updated successfully", "success"))
      .catch(() => snackbarShowMessage("Failed to update billing information", "error"))
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader
        title="Billing Information"
        onEdit={onSave}
        editDisabled={saveDisabled}
      />
      <Divider />
      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Grid item xs={12} sm={5}>
          <LabeledField
            label="Tax ID, e.g. EU Vat *"
            value={financialInfo?.vatNum ?? ""}
            error={errors.vatNum}
            helperText={errors.vatNum ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, vatNum: e.target.value } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <LabeledField
            label="Street *"
            value={address?.street ?? ""}
            error={errors.street}
            helperText={errors.street ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, street: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <LabeledField
            label="Street Number *"
            value={address?.streetNum ?? ""}
            error={errors.streetNum}
            helperText={errors.streetNum ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, streetNum: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <LabeledField
            label="Postal Code *"
            value={address?.postalCode ?? ""}
            error={errors.postalCode}
            helperText={errors.postalCode ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, postalCode: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <LabeledField
            label="City *"
            value={address?.city ?? ""}
            error={errors.city}
            helperText={errors.city ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, city: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <LabeledField
            label="State/Province"
            value={address?.state ?? ""}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, state: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabeledField
            label="Country *"
            value={address?.country ?? ""}
            error={errors.country}
            helperText={errors.country ? "Required" : undefined}
            onChange={(e) =>
              setFinancialInfo({ ...financialInfo, address: { ...address, country: e.target.value } as Address } as FinancialInfoResponse)}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
